<template>
    <div class="user-chooser" :class="{'full-width': fullWidth}">
        <div class="selected-user" :id="id">
            <div class="name">{{ selectedText }}</div>
            <span class="icon far fa-chevron-down"></span>
        </div>
        <elem-dropdown :triggerSelector="`#${id}`" @hide="clearFilter" @show="focusFilter" :fullWidth="fullWidth">
            <template v-slot:default="slotProps">
                <input class="filter" type="text" @click.stop v-model="filterVal" placeholder="Filter" ref="filter" />
                <selectable-list :items="listItems" :value="value" :active="slotProps.isOpen" :onSelect="onSelect" />
            </template>
        </elem-dropdown>
    </div>
</template>
<script>
    import { strings } from '../utils';
    import { mapGetters } from 'vuex';

    export default {
        name: 'UserChooser',
        data() {
            return {
                id: strings.random(),
                filterVal: '',
            };
        },
        props: {
            value: {
                type: String,
                default: '',
            },
            placeholder: {
                type: String,
                default: '',
            },
            noSelectionText: {
                type: String,
                default: '',
            },
            hideAuthUser: {
                type: Boolean,
                default: false,
            },
            hideUsers: {
                type: [Array, String],
            },
            onlyShowAuthorized: {
                type: Boolean,
                default: false,
            },
            hideDisabled: {
                type: Boolean,
                default: true,
            },
            showBoxes: {
                type: Boolean,
                default: true,
            },
            fullWidth: {
                type: Boolean,
                default: false,
            }
        },
        computed: {
            ...mapGetters({
                user: 'user/user',
                users: 'org/users',
                boxes: 'boxes/boxes',
                boxById: 'boxes/boxById',
                authUser: 'user/user',
                variables: 'layout/variables',
            }),
            filterTerms() {
                return this.filterVal.trim().split(/\s+/).map(strings.normalize);
            },
            filteredUsers() {
                if (!this.onlyShowAuthorized) return this.users;

                let accessibleUsers = {};
                for (let userId in this.users) {
                    if (this.accessibleBoxes.find((accessibleBox) => accessibleBox.id === userId)) {
                        accessibleUsers[userId] = this.users[userId];
                    }
                }

                return accessibleUsers;
            },
            filteredBoxes() {
                if (!this.onlyShowAuthorized) return this.boxes.filter((box) => box.isActive);

                return this.boxes.filter((box) => {
                    return box.type === 'org' && this.accessibleBoxes.find((accessibleBox) => accessibleBox.id === box.id);
                });
            },
            accessibleBoxes() {
                return this.$store.getters['boxes/boxesByAccessorId'](this.user.id);
            },
            listItems() {
                let userItems = [];
                if (this.noSelectionText) {
                    userItems.push({ name: this.noSelectionText, value: '' });
                }

                for (let id in this.filteredUsers) {
                    let user = this.users[id];
                    if (this.hideAuthUser && id === this.authUser.id) continue;
                    if (Array.isArray(this.hideUsers) && this.hideUsers.includes(id)) continue;
                    if (this.hideUsers && this.hideUsers === id) continue;
                    if (this.hideDisabled && !user.is_active) continue;

                    let opts = { name: user.name, value: id };
                    if (user.is_locked) {
                        if (!user.is_active) {
                            opts.name += ' (Disabled Account)';
                        } else {
                            opts.name += ' (Locked Account)';
                        }
                        opts.style = {
                            color: this.variables.colorDisabledText,
                        };
                    }
                    userItems.push(opts);
                }

                let allItems = userItems;

                if (this.showBoxes) {
                    let boxItems = this.filteredBoxes
                        .filter((box) => box.type === 'org')
                        .map((box) => {
                            return {
                                name: box.name,
                                value: 'box_' + box.id,
                            };
                        });

                    allItems = allItems.concat(boxItems);
                }

                if (!this.filterTerms.length) return allItems;

                let list = allItems.filter((item) => {
                    let text = strings.normalize(item.name);
                    return !this.filterTerms.filter((term) => text.indexOf(term) === -1).length;
                });

                if (list.length) return list.sort((a, b) => (a.name > b.name ? 1 : -1));
                return [{ name: 'No results', disabled: true }];
            },
            selectedText() {
                if (!this.value || this.value === '-1') return this.placeholder || this.noSelectionText || 'Choose a User';

                let details = this.$store.getters['boxes/parseBoxString'](this.value);

                if (details.type === 'box') {
                    return this.boxById(details.id).name;
                } else {
                    return this.users[details.id].name;
                }
            },
        },
        methods: {
            async focusFilter() {
                await this.$timeout();
                this.$refs.filter.focus();
            },
            clearFilter() {
                setTimeout(() => (this.filterVal = ''), 300);
            },
            onSelect(item) {
                if (item.value !== this.value) {
                    this.$emit('input', item.value);
                    this.$emit('change', item.value);
                }
            },
        },
    };
</script>
<style lang="scss" scoped>
    .user-chooser {
        display: flex;
        height: 100%;
        max-height: $height-button;

        &.error .selected-user {
            border-color: rgba($color-error, 0.6);
            box-shadow: 0 0 3px $color-error;
        }
    }

    .filter {
        margin-bottom: 0.25em;
        width: 100%;
    }

    .selected-user {
        @include border;
        @include border-radius-small;
        background-color: #fff;
        padding: 0.25em 0.5em;
        height: 34px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;

        .name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .icon {
            margin: 0 0.25em;
        }
    }

    ::v-deep .user-chooser:not(.full-width) .list-item {
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
</style>